import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Play,
    Pause,
    Loader2,
    Headphones,
    RotateCcw,
    SkipBack,
    SkipForward,
} from "lucide-react";
import audioManager from "../lib/audioManager";

/**
 * Iter-27.48 / Iter-30.2 — Mini-player audio premium pour le message de
 * bienvenue de l'onboarding.
 *
 * Apporte vs version Iter-27.48 :
 *   - Barre de progression cliquable (seek absolu)
 *   - Boutons "−10s" et "+10s" pour réécouter un passage
 *   - Bouton replay à la fin
 *   - Affichage temps "0:12 / 0:45"
 *   - Loader sur le 1er chargement (~200ms si cache hit, ~5s sinon)
 *   - Stoppe automatiquement quand un podcast/leçon prend le focus (audioManager)
 *   - Cleanup audio à l'unmount + revokeObjectURL
 *
 * Le flow audio reste : GET /api/onboarding/welcome-audio → blob MP3 → cache local.
 * Pas de re-fetch lors d'un replay/seek.
 */
const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

function formatTime(sec) {
    if (!Number.isFinite(sec) || sec < 0) return "0:00";
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
}

export default function WelcomeAudioButton() {
    // Phases :
    //   "idle"      = aucun blob chargé, bouton compact "Écouter le message"
    //   "loading"   = fetch en cours
    //   "ready"     = blob en mémoire, prêt à play (montre le player étendu)
    const [phase, setPhase] = useState("idle");
    const [playing, setPlaying] = useState(false);
    const [current, setCurrent] = useState(0);
    const [duration, setDuration] = useState(0);
    const [seeking, setSeeking] = useState(false);

    const audioRef = useRef(null);
    const blobUrlRef = useRef(null);
    const handleRef = useRef(null);
    if (handleRef.current == null) handleRef.current = audioManager.acquire("onboarding-welcome");

    // Stop quand un autre lecteur prend le focus
    useEffect(() => {
        const unsub = audioManager.subscribe((evt) => {
            if (evt.type === "stopped-by-other" && evt.ownerId === "onboarding-welcome") {
                setPlaying(false);
            }
        });
        return () => {
            unsub();
            if (audioRef.current) {
                try { audioRef.current.pause(); } catch { /* */ }
            }
            if (blobUrlRef.current) {
                try { URL.revokeObjectURL(blobUrlRef.current); } catch { /* */ }
            }
            handleRef.current?.release();
        };
    }, []);

    // Wire les events audio
    const attachAudioEvents = useCallback((audio) => {
        audio.addEventListener("loadedmetadata", () => {
            setDuration(audio.duration || 0);
        });
        audio.addEventListener("timeupdate", () => {
            if (!seeking) setCurrent(audio.currentTime || 0);
        });
        audio.addEventListener("ended", () => {
            setPlaying(false);
            setCurrent(audio.duration || 0);
        });
        audio.addEventListener("pause", () => setPlaying(false));
        audio.addEventListener("play", () => setPlaying(true));
    }, [seeking]);

    const ensureLoaded = useCallback(async () => {
        if (audioRef.current) return audioRef.current;
        setPhase("loading");
        try {
            const res = await fetch(`${API_BASE}/onboarding/welcome-audio`, { credentials: "include" });
            if (!res.ok) throw new Error("welcome audio failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            blobUrlRef.current = url;
            const audio = new Audio(url);
            attachAudioEvents(audio);
            audioRef.current = audio;
            handleRef.current.attach(audio);
            setPhase("ready");
            // Laisse loadedmetadata fixer la durée
            return audio;
        } catch {
            setPhase("idle");
            return null;
        }
    }, [attachAudioEvents]);

    const togglePlay = useCallback(async () => {
        const audio = await ensureLoaded();
        if (!audio) return;
        if (audio.paused) {
            handleRef.current.attach(audio);
            try { await audio.play(); } catch { /* AbortError silencieux */ }
        } else {
            audio.pause();
        }
    }, [ensureLoaded]);

    const skipBy = useCallback((deltaSec) => {
        const audio = audioRef.current;
        if (!audio || !duration) return;
        const next = Math.max(0, Math.min(duration, (audio.currentTime || 0) + deltaSec));
        audio.currentTime = next;
        setCurrent(next);
    }, [duration]);

    const restart = useCallback(async () => {
        const audio = await ensureLoaded();
        if (!audio) return;
        audio.currentTime = 0;
        setCurrent(0);
        handleRef.current.attach(audio);
        try { await audio.play(); } catch { /* */ }
    }, [ensureLoaded]);

    const onSeek = useCallback((e) => {
        const audio = audioRef.current;
        if (!audio || !duration) return;
        const ratio = Math.max(0, Math.min(1, Number(e.target.value) / 100));
        const next = ratio * duration;
        audio.currentTime = next;
        setCurrent(next);
    }, [duration]);

    // Render compact (idle) — bouton classique pour le 1er clic
    if (phase === "idle" || phase === "loading") {
        return (
            <motion.button
                initial={{ scale: 0.92, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.35, duration: 0.45 }}
                onClick={togglePlay}
                disabled={phase === "loading"}
                data-testid="onboarding-welcome-audio-btn"
                className="mt-5 inline-flex items-center gap-2.5 px-5 py-3 rounded-full font-extrabold text-sm transition-all shadow-lg backdrop-blur bg-white/15 text-white border border-white/30 hover:bg-white/25 hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed"
                title="Écouter le message de bienvenue (voix Shariti)"
            >
                {phase === "loading" ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                    <div className="flex items-center gap-2">
                        <Headphones className="w-4 h-4" />
                        <Play className="w-3.5 h-3.5 fill-current" />
                    </div>
                )}
                <span>{phase === "loading" ? "Chargement..." : "Écouter le message de bienvenue"}</span>
            </motion.button>
        );
    }

    // Render player étendu (ready) — barre + skip ±10s + play/pause
    const progressPct = duration ? Math.min(100, (current / duration) * 100) : 0;
    const ended = duration > 0 && current >= duration - 0.1 && !playing;

    return (
        <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.35 }}
            data-testid="onboarding-welcome-audio-player"
            className="mt-5 w-full max-w-md mx-auto rounded-2xl bg-black/40 backdrop-blur-md border border-white/15 px-4 py-3 shadow-2xl shadow-black/40"
        >
            {/* Top row : icon + label + temps */}
            <div className="flex items-center justify-between mb-2.5 text-[10px] font-extrabold tracking-[0.18em] uppercase">
                <div className="flex items-center gap-1.5 text-white/75">
                    <Headphones className="w-3.5 h-3.5" />
                    <span>Message de bienvenue</span>
                </div>
                <span className="text-white/55 font-mono tabular-nums tracking-normal text-[11px]" data-testid="welcome-audio-time">
                    {formatTime(current)} / {formatTime(duration)}
                </span>
            </div>

            {/* Seekbar */}
            <div className="relative mb-3 group">
                <input
                    type="range"
                    min="0"
                    max="100"
                    step="0.1"
                    value={progressPct}
                    onChange={onSeek}
                    onMouseDown={() => setSeeking(true)}
                    onMouseUp={() => setSeeking(false)}
                    onTouchStart={() => setSeeking(true)}
                    onTouchEnd={() => setSeeking(false)}
                    data-testid="welcome-audio-seekbar"
                    className="welcome-audio-range w-full"
                    style={{
                        background: `linear-gradient(to right, #EE722E 0%, #EE722E ${progressPct}%, rgba(255,255,255,0.15) ${progressPct}%, rgba(255,255,255,0.15) 100%)`,
                    }}
                    aria-label="Position de lecture du message de bienvenue"
                />
                <style>{`
                    .welcome-audio-range {
                        -webkit-appearance: none;
                        appearance: none;
                        height: 5px;
                        border-radius: 999px;
                        outline: none;
                        cursor: pointer;
                    }
                    .welcome-audio-range::-webkit-slider-thumb {
                        -webkit-appearance: none;
                        appearance: none;
                        width: 14px;
                        height: 14px;
                        border-radius: 50%;
                        background: #FFFFFF;
                        border: 2px solid #EE722E;
                        cursor: pointer;
                        box-shadow: 0 2px 6px rgba(0,0,0,0.4);
                        transition: transform 0.15s ease;
                    }
                    .welcome-audio-range:hover::-webkit-slider-thumb,
                    .welcome-audio-range:active::-webkit-slider-thumb {
                        transform: scale(1.2);
                    }
                    .welcome-audio-range::-moz-range-thumb {
                        width: 14px;
                        height: 14px;
                        border-radius: 50%;
                        background: #FFFFFF;
                        border: 2px solid #EE722E;
                        cursor: pointer;
                        box-shadow: 0 2px 6px rgba(0,0,0,0.4);
                    }
                `}</style>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-3 sm:gap-4 px-1.5">
                <button
                    type="button"
                    onClick={() => skipBy(-10)}
                    data-testid="welcome-audio-skip-back"
                    aria-label="Reculer de 10 secondes"
                    className="relative shrink-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center text-white/85 hover:text-white transition-all active:scale-95"
                    title="−10s"
                >
                    <SkipBack className="w-4 h-4" />
                </button>

                <AnimatePresence mode="wait" initial={false}>
                    {ended ? (
                        <motion.button
                            key="replay"
                            type="button"
                            onClick={restart}
                            data-testid="welcome-audio-replay"
                            aria-label="Réécouter depuis le début"
                            initial={{ scale: 0.85, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.85, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="w-14 h-14 rounded-full bg-gradient-to-br from-shariti-orange to-shariti-yellow text-white flex items-center justify-center shadow-lg shadow-shariti-orange/30 hover:scale-105 active:scale-95 transition-all orange-glow"
                            title="Réécouter"
                        >
                            <RotateCcw className="w-5 h-5" />
                        </motion.button>
                    ) : (
                        <motion.button
                            key="play"
                            type="button"
                            onClick={togglePlay}
                            data-testid="welcome-audio-play-pause"
                            aria-label={playing ? "Pause" : "Lire"}
                            initial={{ scale: 0.85, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.85, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="w-14 h-14 rounded-full bg-gradient-to-br from-shariti-orange to-shariti-yellow text-white flex items-center justify-center shadow-lg shadow-shariti-orange/30 hover:scale-105 active:scale-95 transition-all orange-glow"
                            title={playing ? "Pause" : "Lire"}
                        >
                            {playing ? (
                                <Pause className="w-5 h-5 fill-white" />
                            ) : (
                                <Play className="w-5 h-5 fill-white ml-0.5" />
                            )}
                        </motion.button>
                    )}
                </AnimatePresence>

                <button
                    type="button"
                    onClick={() => skipBy(10)}
                    data-testid="welcome-audio-skip-forward"
                    aria-label="Avancer de 10 secondes"
                    className="relative shrink-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center text-white/85 hover:text-white transition-all active:scale-95"
                    title="+10s"
                >
                    <SkipForward className="w-4 h-4" />
                </button>
            </div>
        </motion.div>
    );
}
